import React, { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CloseIcon } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";

import type { getAnalyticsReportIProps } from "~/entity/Ivideon/hooks/useGetAnalyticaReport";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";

import FastFilter from "./FastFilter";
import DatePicker from "./ui/DatePicker";
import Select from "./ui/Select";
import ZoneFilter, {
  ZONE_FILTER_DEALER_ID,
  type ZoneFilterValue,
} from "./ZoneFilter";
import { parseForServer } from "./helper";
import {
  getConfigFields,
  getDefaultFilter,
  type GetDefaultFilterIProps,
} from "./options";
import { validationSchema } from "./schema";
import type {
  DealerInfoIProps,
  FormValues,
  OptionsGroupIProps,
  ParseForServerResultIProps,
} from "./schema/types";
import {
  filtersFromURL,
  filtersToURL,
  type SummaryURLParams,
} from "./urlFilters";

import styles from "./FilterSummary.less";

interface FormFilterIProps {
  options: OptionsGroupIProps;
  setParamsFilter: (props: ParseForServerResultIProps) => void;
  dealerInfo: DealerInfoIProps;
  getAnalyticsReport: (data: getAnalyticsReportIProps) => void;
  type: "face" | "cars";
}

const FormFilter = ({
  options,
  setParamsFilter,
  dealerInfo,
  getAnalyticsReport,
  type,
}: FormFilterIProps) => {
  const setURLParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<SummaryURLParams>();

  // Read the URL only once on mount to build the form's initial defaultValues.
  // Subsequent changes to urlParams/options are intentionally ignored: from
  // this point on the URL is driven by the form, not the other way around.
  const initialFromURL = useMemo(
    () => filtersFromURL(urlParams, options, getDefaultFilter({ dealerInfo })),
    [],
  );

  const [fastItem, setFastItem] = useState<"day" | "week" | "month" | null>(
    initialFromURL ? initialFromURL.fastItem : "day",
  );
  const [zone, setZone] = useState<ZoneFilterValue>("all");

  const isMounted = useRef(false);

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: initialFromURL
      ? initialFromURL.values
      : getDefaultFilter({ dealerInfo }),
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const watchedDealerSelect = watch("dealerSelect");
  const watchedSelectFrom = watch("selectFrom");

  const isZoneFilterVisible = useMemo(() => {
    if (type !== "face" || watchedSelectFrom?.value !== "dealer") return false;
    const selected = Array.isArray(watchedDealerSelect)
      ? watchedDealerSelect
      : watchedDealerSelect
        ? [watchedDealerSelect]
        : [];
    return (
      selected.length === 1 &&
      Number(selected[0].value) === ZONE_FILTER_DEALER_ID
    );
  }, [type, watchedSelectFrom?.value, watchedDealerSelect]);

  const buildParamsFilter = (
    values: FormValues,
    zoneValue: ZoneFilterValue = zone,
  ): ParseForServerResultIProps => {
    const parsed = parseForServer(values);
    if (isZoneFilterVisible && zoneValue !== "all") {
      parsed.zone = zoneValue;
    }
    return parsed;
  };

  const applyZoneFilter = (newZone: ZoneFilterValue) => {
    setZone(newZone);
    setParamsFilter(buildParamsFilter(getValues(), newZone));
  };

  useEffect(() => {
    if (!isZoneFilterVisible && zone !== "all") {
      setZone("all");
    }
  }, [isZoneFilterVisible]);

  const applyFastFilter = (
    getRange: () => [Date, Date],
    newFastItem: "day" | "week" | "month",
  ) => {
    const isTurningOff = fastItem === newFastItem;

    if (isTurningOff) {
      const defaultValues = getDefaultFilter({ dealerInfo });
      setValue("startDate", defaultValues.startDate);
      setValue("endDate", defaultValues.endDate);
      setFastItem(null);

      const currentData = {
        ...getValues(),
        startDate: defaultValues.startDate,
        endDate: defaultValues.endDate,
      };
      setParamsFilter(buildParamsFilter(currentData));
      setURLParams(filtersToURL(currentData, null));
    } else {
      const [start, end] = getRange();
      setValue("startDate", start);
      setValue("endDate", end);
      setFastItem(newFastItem);
      const currentData = {
        ...getValues(),
        startDate: start,
        endDate: end,
      };
      setParamsFilter(buildParamsFilter(currentData));
      setURLParams(filtersToURL(currentData, newFastItem));
    }
  };

  useEffect(() => {
    const currentValues = getValues();
    setParamsFilter(buildParamsFilter(currentValues));
  }, []);

  const selectedCategory = watchedSelectFrom;
  const { startDate, endDate } = watch(["startDate", "endDate"]);

  const currentCategory = selectedCategory?.value as keyof OptionsGroupIProps;
  const dynamicOptions = options[currentCategory] || [];
  const dynamicOptionsSignature = dynamicOptions
    .map((option) => `${option.value}:${option.label}`)
    .join("|");

  const resetFilter = () => {
    const defaultFilter = getDefaultFilter({ dealerInfo });
    reset({ ...defaultFilter });
    setFastItem("day");
    setZone("all");
    setParamsFilter(buildParamsFilter(defaultFilter, "all"));
    setURLParams(filtersToURL(defaultFilter, "day"));
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const currentKey = selectedCategory?.value as GetDefaultFilterIProps["key"];

    if (currentKey) {
      const defaults = getDefaultFilter({ dealerInfo, key: currentKey });

      setValue("dealerSelect", defaults.dealerSelect, {
        shouldValidate: true,
        shouldDirty: true,
      });
      if (currentKey !== "dealer") {
        setFastItem(null);
      }
    }
  }, [selectedCategory?.value, dealerInfo, setValue]);

  useEffect(() => {
    const current = getValues("dealerSelect");
    if (!Array.isArray(current) || current.length === 0) return;
    if (!dynamicOptions || dynamicOptions.length === 0) return;

    const dynamicOptionsByValue = new Map(
      dynamicOptions.map((option) => [String(option.value), option]),
    );

    let changed = false;
    const remapped = current.map((item) => {
      const match = dynamicOptionsByValue.get(String(item.value));
      if (match && match.label !== item.label) {
        changed = true;
        return match;
      }
      return item;
    });

    if (changed) {
      setValue("dealerSelect", remapped, { shouldValidate: true });
    }
    // dynamicOptionsSignature captures value+label pairs so the effect also
    // reruns when the options list content changes without its length changing.
  }, [dynamicOptionsSignature, currentCategory]);

  const onFormSubmit = (data: FormValues) => {
    setParamsFilter(buildParamsFilter(data));
    setURLParams(filtersToURL(data, fastItem));
  };

  const handleGenerateReport = () => {
    getAnalyticsReport({
      type,
      values: getValues(),
      filterMode: fastItem,
    });
  };

  return (
    <div
      className={styles.wrapper_filter_summary}
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <form>
        <div className={styles.filter_summary}>
          {options &&
            Object.entries(getConfigFields(dynamicOptions)).map(
              ([fieldName, fieldConfig]) => {
                switch (fieldConfig.type) {
                  case "select":
                    return (
                      <Select
                        key={fieldName}
                        control={control}
                        fieldConfig={fieldConfig}
                        fieldName={fieldName}
                        setFastItem={setFastItem}
                      />
                    );
                  case "range":
                    if (!fieldConfig.fields) return null;
                    const { start, end } = fieldConfig.fields;
                    return (
                      <div className={styles.filter_dates} key={fieldName}>
                        <span>{fieldConfig.label}</span>
                        <div className={styles.dates}>
                          <DatePicker
                            control={control}
                            format="DD.MM.YYYY"
                            name={start}
                            placeholder="от дд.мм.гггг"
                            setFastItem={setFastItem}
                            watchAllFields={{ startDate, endDate }}
                          />
                          <DatePicker
                            control={control}
                            format="DD.MM.YYYY"
                            name={end}
                            placeholder="до дд.мм.гггг"
                            setFastItem={setFastItem}
                            watchAllFields={{ startDate, endDate }}
                          />
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              },
            )}
          <Button
            className={styles.resetFilterBtn}
            color="secondary"
            variant="filled"
            size="small"
            radius="30"
            icon={<CloseIcon size={16} />}
            bold={false}
            htmlType="button"
            onClick={resetFilter}
            aria-label="Сбросить фильтры"
          />
        </div>
        <div className={styles.custombtn}>
          <Button
            className={styles.generateReportBtn}
            color="secondary"
            size="middle"
            fontSize="12"
            htmlType="button"
            onClick={handleGenerateReport}
          >
            Сформировать отчет
          </Button>
          <Button
            color="primary"
            size="middle"
            fontSize="12"
            htmlType="submit"
            disabled={!isValid}
          >
            Применить
          </Button>
        </div>
      </form>
      <FastFilter applyFastFilter={applyFastFilter} fastItem={fastItem} />
      {isZoneFilterVisible && (
        <ZoneFilter applyZoneFilter={applyZoneFilter} zone={zone} />
      )}
    </div>
  );
};

export default FormFilter;
